import React from 'react';

import { useNavigate } from 'react-router';

import { ROUTES } from '@/_navigation';
import { useAuth } from '@/_providers';
import { CookieManager, clearChat } from '@/_services';

import type { LoginApiResponse } from './api';
import { useGoogleLogin, useLoginMutation, useFacebookLogin, LOGIN_API_ERROR } from './api';
import type { LoginApiErrorResponse } from './api/types';
import type { TLoginForm } from './form';
import { useFormDef } from './form';

export const useLoginPageLogic = () => {
  const nav = useNavigate();
  const { login: authLogin } = useAuth();
  const [LoginErrorNode, setLoginErrorNode] = React.useState<React.ReactNode>(null);

  const form = useFormDef();

  const handleLoginTokens = React.useCallback(
    (accessToken: string, refreshToken: string) => {
      authLogin({ accessToken, refreshToken, loginMethod: 'normal' });
      clearChat();
      nav({ pathname: ROUTES.RESOLVE_LOGIN.path }, { state: ROUTES.RESOLVE_LOGIN.buildState({ from: 'LOGIN' }) });
    },
    [authLogin, nav]
  );

  const handleLoginSuccess = React.useCallback(
    (data: LoginApiResponse) => {
      if (!data) {
        return;
      }
      handleLoginTokens(data.access, data.refresh);
    },
    [handleLoginTokens]
  );

  const handleLoginError = React.useCallback((data: LoginApiErrorResponse) => {
    if (data.message === LOGIN_API_ERROR.INSTRUCTOR_ACCOUNT_EXISTS.code) {
      setLoginErrorNode(
        LOGIN_API_ERROR.INSTRUCTOR_ACCOUNT_EXISTS.ErrorNode({
          onReset: () => setLoginErrorNode(null),
        })
      );
    }
  }, []);

  const { login, isLoading: isLoadingLogin } = useLoginMutation({
    onSuccess: handleLoginSuccess,
    onError: handleLoginError,
  });

  const {
    initGoogleLogin,
    isLoading: isLoadingGoogleLogin,
    ErrorNode: GoogleErrorNode,
  } = useGoogleLogin({
    onSuccess: handleLoginSuccess,
    onError: handleLoginError,
  });

  const {
    initLogin: initFacebookLogin,
    isLoading: isLoadingFacebookLogin,
    isLoaded,
  } = useFacebookLogin({
    onSuccess: handleLoginSuccess,
    onError: handleLoginError,
  });

  const onSubmit = async ({ email, password }: TLoginForm) => {
    try {
      await login({ username: email, password, checked: true });
      CookieManager.setLastUsername(email);
      CookieManager.setLastPassword(password);
      CookieManager.setOnboardingModal(true);
    } catch (error) {
      console.error('Unable to login', error);
    }
  };

  const handleForgotPasswordClick = () => {
    nav(ROUTES.FORGET_PASSWORD.path);
  };

  const onGoogleLogin = () => {
    initGoogleLogin();
  };

  const onFacebookLogin = () => {
    initFacebookLogin();
  };

  return {
    form,
    onSubmit,
    isLoading: isLoadingLogin,
    handleForgotPasswordClick,
    onGoogleLogin,
    isLoadingGoogleLogin,
    ErrorNode: LoginErrorNode || GoogleErrorNode,
    onFacebookLogin,
    isLoadingFacebookLogin,
    facebookLoginDisabled: isLoadingFacebookLogin || !isLoaded,
  };
};
